import React from 'react';
import { Box, Stack } from '@mui/material';
import { anchorRef, bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';

import {
  SearchForm,
  SearchFormProps,
  TableContainer,
  TableHeader,
  FilterButton,
  isFiltersSelected,
  Tab,
  Tabs,
} from '@occc/ui-design-system';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { useTabQueryParam } from '@occc/utils-query-params';

import { OffersAndVouchersSectionTab } from './types';
import OffersContainer from './Offers';
import VouchersContainer from './Vouchers';
import { useEligibility, useEngagementType, useVisibleStatus } from './Offers/hooks';
import { useSourceTypeFilter, useStateFilter } from './Vouchers/hooks';
import { InProgressOffersContainer } from './InProgressOffers/components';
import { useProgressOffersEligibility } from './InProgressOffers/in-progress-offers.hook';
import {
  OFFERS_SECTION_TAB_PARAM,
  DEFAULT_OFFERS_SECTION_TAB,
  OFFERS_SECTION_TABS,
  titleByTab,
} from './constants';

import {
  IN_PROGRESS_OFFERS_PARAM_NAMES,
  OFFERS_PARAM_NAMES,
  VOUCHERS_PARAM_NAMES,
} from '../../constants';

export type OffersAndVouchersProps = {
  memberId: string;
};

const transitionStyle = 'transform 500ms cubic-bezier(.75, 0, .25, 1)';
const targetingBoxStyles = {
  transition: transitionStyle,
  overflow: 'hidden',
  width: '100%',
};

const targetingCommonStyles = (isActive: boolean) => ({
  transition: transitionStyle,
  width: '100%',
  overflow: 'hidden',
  maxHeight: isActive ? '100%' : '0px',
});

const leftTabStyles = (isActive: boolean) => ({
  ...targetingCommonStyles(isActive),
  transform: isActive ? 'translate(0, 0)' : 'translate(-100%, 0)',
});

const rightTabStyles = (isActive: boolean) => ({
  ...targetingCommonStyles(isActive),
  transform: isActive ? 'translate(0, 0)' : 'translate(100%, 0)',
});
export const OffersAndVouchers: React.FC<OffersAndVouchersProps> = ({ memberId }) => {
  const [activeTab, setActiveTab] = useTabQueryParam<OffersAndVouchersSectionTab>(
    OFFERS_SECTION_TAB_PARAM,
    DEFAULT_OFFERS_SECTION_TAB,
    OFFERS_SECTION_TABS,
  );

  const [offersSearch, setOffersSearch] = useSearchParams(OFFERS_PARAM_NAMES);
  const [inProgressOffersSearch, setInProgressOffersSearch] = useSearchParams(
    IN_PROGRESS_OFFERS_PARAM_NAMES,
  );
  const [vouchersSearch, setVouchersSearch] = useSearchParams(VOUCHERS_PARAM_NAMES);

  const onOffersSearchSubmit: SearchFormProps['onSubmit'] = ({ search: value }) => {
    setOffersSearch(value);
  };

  const onInProgressOffersSearchSubmit: SearchFormProps['onSubmit'] = ({ search: value }) => {
    setInProgressOffersSearch(value);
  };

  const onVouchersSearchSubmit: SearchFormProps['onSubmit'] = ({ search: value }) => {
    setVouchersSearch(value);
  };

  const handleTabChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: OffersAndVouchersSectionTab,
  ) => {
    setActiveTab(value);
  };

  const offersPopupState = usePopupState({ variant: 'popover', popupId: 'offersFilters' });
  const vouchersPopupState = usePopupState({ variant: 'popover', popupId: 'vouchersFilters' });
  const [engagementType] = useEngagementType();
  const [eligibility] = useEligibility();
  const [visibleStatus] = useVisibleStatus();
  const [sourceType] = useSourceTypeFilter();
  const [voucherState] = useStateFilter();

  const [progressOffersEligibility] = useProgressOffersEligibility();
  const inProgressOffersPopupState = usePopupState({
    variant: 'popover',
    popupId: 'inProgressOffersFilters',
  });

  const showBadgeOffers = isFiltersSelected([engagementType, eligibility, visibleStatus], ['any']);
  const showBadgeInProgressOffers = progressOffersEligibility !== 'eligible';
  const showBadgeVouchers = isFiltersSelected([sourceType, voucherState], ['any']);

  const offersPopoverProps = {
    ...bindPopover({
      ...offersPopupState,
      isOpen: offersPopupState.isOpen && activeTab === 'offers',
    }),
  };

  const inProgressOffersPopoverProps = {
    ...bindPopover({
      ...inProgressOffersPopupState,
      isOpen: inProgressOffersPopupState.isOpen && activeTab === 'inProgress',
    }),
  };

  const vouchersPopoverProps = {
    ...bindPopover({
      ...vouchersPopupState,
      isOpen: vouchersPopupState.isOpen && activeTab === 'vouchers',
    }),
  };

  const renderOffersActions = () => (
    <Stack direction='row' alignItems='center' gap={1}>
      <SearchForm onSubmit={onOffersSearchSubmit} initialValues={{ search: offersSearch }} />
      <FilterButton
        {...bindTrigger(offersPopupState)}
        ref={anchorRef(offersPopupState)}
        showBadge={showBadgeOffers}
      />
    </Stack>
  );

  const renderInProgressOffersActions = () => (
    <Stack direction='row' alignItems='center' gap={1}>
      <SearchForm
        onSubmit={onInProgressOffersSearchSubmit}
        initialValues={{ search: inProgressOffersSearch }}
      />
      <FilterButton
        {...bindTrigger(inProgressOffersPopupState)}
        ref={anchorRef(inProgressOffersPopupState)}
        showBadge={showBadgeInProgressOffers}
      />
    </Stack>
  );

  const renderVouchersActions = () => {
    return (
      <Stack direction='row' alignItems='center' gap={1}>
        <SearchForm onSubmit={onVouchersSearchSubmit} initialValues={{ search: vouchersSearch }} />
        <FilterButton
          {...bindTrigger(vouchersPopupState)}
          ref={anchorRef(vouchersPopupState)}
          showBadge={showBadgeVouchers}
        />
      </Stack>
    );
  };

  const renderActions = () => {
    if (activeTab === 'offers') {
      return renderOffersActions();
    }

    if (activeTab === 'inProgress') {
      return renderInProgressOffersActions();
    }

    if (activeTab === 'vouchers') {
      return renderVouchersActions();
    }

    return null;
  };

  return (
    <TableContainer sx={{ boxShadow: 'none' }}>
      <TableHeader
        disableTitleTypography
        tabs={
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab value={'offers' satisfies OffersAndVouchersSectionTab} label={titleByTab.offers} />
            <Tab
              value={'inProgress' satisfies OffersAndVouchersSectionTab}
              label={titleByTab.inProgress}
            />
            <Tab
              value={'vouchers' satisfies OffersAndVouchersSectionTab}
              label={titleByTab.vouchers}
            />
          </Tabs>
        }
        actions={renderActions()}
      />
      <Box style={targetingBoxStyles}>
        <Box
          id={'offers' satisfies OffersAndVouchersSectionTab}
          style={leftTabStyles(activeTab === 'offers')}
        >
          <OffersContainer
            memberId={memberId}
            searchText={offersSearch}
            popoverProps={offersPopoverProps}
          />
        </Box>

        <Box
          id={'inProgress' satisfies OffersAndVouchersSectionTab}
          style={
            activeTab === 'offers'
              ? rightTabStyles(false)
              : leftTabStyles(activeTab === 'inProgress')
          }
        >
          <InProgressOffersContainer
            memberId={memberId}
            searchText={inProgressOffersSearch}
            popoverProps={inProgressOffersPopoverProps}
          />
        </Box>

        <Box
          id={'vouchers' satisfies OffersAndVouchersSectionTab}
          style={rightTabStyles(activeTab === 'vouchers')}
        >
          <VouchersContainer
            memberId={memberId}
            searchText={vouchersSearch}
            popoverProps={vouchersPopoverProps}
          />
        </Box>
      </Box>
    </TableContainer>
  );
};
